import { Trans } from "@lingui/macro";
import { useRubriek } from "components/aangifte/useRubriek";
import { FormikNumberField } from "components/fields/FormikNumberField";
import { FormGroup } from "components/FormGroup";
import { useWhiteLabelSetting } from "components/WhiteLabelProvider";
import { rubriekFp } from "domain/rubriek";
import { Form, FormikProvider, useFormik } from "formik";
import { option } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { useSessionStorage } from "hooks/useSessionStorage";
import React, { FunctionComponent } from "react";
import * as Yup from "yup";

type FieldType =
  | "kapitaalaflossing"
  | "bedragLening"
  | "jaarLening"
  | "KTLJaarNaLening"
  | "premie"
  | "verzekerdBedrag"
  | "verzekerdBedragAangever"
  | "verzekerdBedragPartner";

type Values = Partial<Record<FieldType, number>>;
export interface Props {
  code: string;
  fields: FieldType[];
  onSubmit: (values: Values) => void;
}

const jaarLeningErrorMessage: any = (
  <Trans>Het jaar van lening moet zich bevinden tussen 1989 en {new Date().getFullYear() - 1}.</Trans>
);

const schema = Yup.object({
  jaarLening: Yup.number()
    .typeError(jaarLeningErrorMessage)
    .min(1989, jaarLeningErrorMessage)
    .max(new Date().getFullYear() - 1, jaarLeningErrorMessage),
});

export const HelpForm: FunctionComponent<Props> = ({ code, onSubmit, fields }) => {
  const inputRef = React.useRef<HTMLInputElement>();
  const companyName = useWhiteLabelSetting("name") as string;
  const [sessionValues, storeSessionValues] = useSessionStorage(`${companyName}.${code}`, {});
  const rubriek = useRubriek(code);

  const initialValues = pipe(
    rubriek,
    option.fold(
      () => sessionValues,
      (r) => (rubriekFp.hasValue(r) ? sessionValues : {})
    )
  );

  const handleSubmit = (values: Values) => {
    storeSessionValues(values);
    onSubmit(values);
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema: schema,
  });

  const {
    values: { jaarLening },
  } = formik;

  React.useEffect(() => {
    if (inputRef.current) {
      const ref = inputRef.current;
      setTimeout(() => {
        ref.focus();
      }, 0);
    }
  }, []);

  return (
    <FormikProvider value={formik}>
      <Form>
        {fields.map((field, index) => {
          const ref = index === 0 ? inputRef : undefined;
          switch (field) {
            case "kapitaalaflossing":
              return (
                <FormGroup name={"kapitaalaflossing"} key={field}>
                  <label htmlFor={"kapitaalaflossing"}>
                    <Trans>Kapitaalaflossing</Trans>
                  </label>
                  <FormikNumberField name={"kapitaalaflossing"} getInputRef={ref} />
                </FormGroup>
              );
            case "bedragLening":
              return (
                <FormGroup name={"bedragLening"} key={field}>
                  <label htmlFor={"bedragLening"}>
                    <Trans>Hypothecair ontleend bedrag</Trans>
                  </label>
                  <FormikNumberField name={"bedragLening"} getInputRef={ref} />
                </FormGroup>
              );
            case "jaarLening":
              return (
                <FormGroup name={"jaarLening"} key={field}>
                  <label htmlFor={"jaarLening"}>
                    <Trans>Jaar van lening</Trans>
                  </label>
                  <FormikNumberField name={"jaarLening"} max={4} decimalScale={0} getInputRef={ref} />
                </FormGroup>
              );
            case "KTLJaarNaLening":
              return (
                <FormGroup name={"KTLJaarNaLening"} key={field}>
                  <label htmlFor={"KTLJaarNaLening"}>
                    <Trans>
                      Kinderen ten laste op 01/01/
                      {!!jaarLening ? jaarLening + 1 : "xxxx"}
                    </Trans>
                  </label>
                  <FormikNumberField name={"KTLJaarNaLening"} min={0} decimalScale={0} getInputRef={ref} />
                </FormGroup>
              );
            case "premie":
              return (
                <FormGroup name={"premie"} key={field}>
                  <label htmlFor={"premie"}>
                    <Trans>Premie levensverzekering</Trans>
                  </label>
                  <FormikNumberField name={"premie"} getInputRef={ref} />
                </FormGroup>
              );
            case "verzekerdBedragAangever":
              return (
                <FormGroup name={"verzekerdBedragAangever"} key={field}>
                  <label htmlFor={"verzekerdBedragAangever"}>
                    <Trans>Verzekerd bedrag aangever</Trans>
                  </label>
                  <FormikNumberField name={"verzekerdBedragAangever"} getInputRef={ref} />
                </FormGroup>
              );
            case "verzekerdBedragPartner":
              return (
                <FormGroup name={"verzekerdBedragPartner"} key={field}>
                  <label htmlFor={"verzekerdBedragPartner"}>
                    <Trans>Verzekerd bedrag partner</Trans>
                  </label>
                  <FormikNumberField name={"verzekerdBedragPartner"} getInputRef={ref} />
                </FormGroup>
              );
            case "verzekerdBedrag":
              return (
                <FormGroup name={"verzekerdBedrag"} key={field}>
                  <label htmlFor={"verzekerdBedrag"}>
                    <Trans>Verzekerd bedrag</Trans>
                  </label>
                  <FormikNumberField name={"verzekerdBedrag"} getInputRef={ref} />
                </FormGroup>
              );
            default:
              return null;
          }
        })}

        <button type="submit">
          <Trans>Voeg toe</Trans>
        </button>
      </Form>
    </FormikProvider>
  );
};

HelpForm.displayName = "HelpForm";
